import {
  IRD_NODE_TYPES,
  MAX_CATEGORIES_NESTED,
  MAX_CATEGORIES_NESTED_ROOT,
  MAX_CHILDREN,
  MAX_DEPTH,
} from '@sonifi/wb-in-room-dining/constants/wbInRoomDiningConstants';
import { PLATFORMS } from '@sonifi/wb-screenbuilder/constants/wbScreenBuilderConstants';
import { findNodeParent, isNodeRoot } from '@sonifi/wb-tree/utils/helper';
import { IRD_MENU_NODES_PER_PAGE } from './irdMenuSliceConstants';

export const findNodesByPlatform = ({ menu, node, platform }) => {
  const nodeParent = findNodeParent({ menu, node });
  const nodeIsRoot = isNodeRoot({
    menu,
    nodeId: node.id,
  });
  const parentIsRoot = nodeParent
    ? isNodeRoot({ menu, nodeId: nodeParent.id })
    : false;
  const nodePhonePreviewSelected =
    nodeIsRoot || parentIsRoot ? node : nodeParent;
  const nodeTvPreviewSelected = nodeIsRoot
    ? node.children[0]
    : parentIsRoot
    ? node
    : nodeParent;
  const nodePreviewSelected =
    platform === PLATFORMS.tv
      ? nodeTvPreviewSelected
      : nodePhonePreviewSelected;

  return { nodeParent, nodePreviewSelected };
};

export const createVisibleChildren = ({
  actualPage,
  nodeSelected,
  nodesPerPage,
}) => {
  const visibleChildren = nodeSelected.children
    ? nodeSelected.children
        .filter((node) => node.active)
        .slice(
          actualPage * nodesPerPage,
          actualPage * nodesPerPage + nodesPerPage,
        )
    : [];
  return visibleChildren;
};

export const getPagination = ({ nodeSelected, platform }) => {
  const nodeType = nodeSelected.isRoot ? 'categories' : 'items';
  const nodesPerPage = IRD_MENU_NODES_PER_PAGE[platform][nodeType];
  const maxPage = nodeSelected.children
    ? Math.max(
        Math.ceil(
          nodeSelected.children.filter((node) => node.active).length /
            nodesPerPage,
        ) - 1,
        0,
      )
    : 0;

  return {
    nodesPerPage,
    maxPage,
  };
};

export const showEditIcon = ({ isHealthcare, item }) => {
  if (isHealthcare && item.type === IRD_NODE_TYPES.ird_categories) {
    return false;
  }
  return true;
};

const doesNodeHaveUnpublishedChanges = (nodeSubject, lastPublished) => {
  let haveOptionSetChanges = false;
  const currentLastPublished = lastPublished ? new Date(lastPublished) : null;
  if (nodeSubject.ird_option_sets && currentLastPublished) {
    let publishDate;
    nodeSubject.ird_option_sets.some((option, i) => {
      publishDate = option.last_updated ? new Date(option.last_updated) : null;
      haveOptionSetChanges = publishDate > currentLastPublished;

      // I ask this because if its true we don't need to iterate the options array.
      if (haveOptionSetChanges) {
        return haveOptionSetChanges;
      }

      option?.ird_items?.some((item, j) => {
        publishDate = item.last_updated ? new Date(item.last_updated) : null;
        haveOptionSetChanges = publishDate > currentLastPublished;
        return haveOptionSetChanges;
      });

      return haveOptionSetChanges;
    });
  }

  const currentNodeLastUpdated = nodeSubject.last_updated
    ? new Date(nodeSubject.last_updated)
    : null;
  return haveOptionSetChanges || currentNodeLastUpdated > currentLastPublished;
};

export const validate = ({ node, lastPublished }) => {
  if (node?.subject?.hasDeletedContent) {
    return 'References deleted content';
  } else if (doesNodeHaveUnpublishedChanges(node?.subject, lastPublished)) {
    return 'Has unpublished changes';
  }
};

export const isBranch = (node) =>
  (node.subject.isZombie && !node.children?.length) ||
  node.type === IRD_NODE_TYPES.ird_categories;

export const getHasUnpublishedChanges = ({
  irdMenu,
  lastPublished,
  productSettings,
}) =>
  new Date(irdMenu.last_updated) > lastPublished ||
  new Date(productSettings.last_updated) > lastPublished;

export const getPublishStatusMessage = ({
  lastPublishJob,
  isIncompleteJob,
  isFailedJob,
}) => {
  if (isFailedJob) {
    return 'Failed to publish on:';
  }
  if (isIncompleteJob) {
    return 'Publish request submitted on:';
  }
  if (!lastPublishJob) {
    return 'Not yet published';
  }
  return 'Last Published:';
};

export const findAndUpdateNodes = ({ children, nodes, lastUpdated }) => {
  const childrenRes = children.map((ch) => {
    if (ch.children) {
      return {
        ...ch,
        children: findAndUpdateNodes({ children: ch.children, nodes }),
        subject: {
          ...ch.subject,
          last_updated: nodes.some((node) => node.id === ch.id)
            ? lastUpdated
            : ch.subject.last_updated,
        },
      };
    } else {
      return {
        ...ch,
        subject: {
          ...ch.subject,
          last_updated: nodes.some((node) => node.id === ch.id)
            ? lastUpdated
            : ch.subject.last_updated,
        },
      };
    }
  });
  return childrenRes;
};

const isBeyondMaxDepth = (depth) => depth >= MAX_DEPTH;

export const isBeyondMaxChildren = (childrenCount) =>
  childrenCount >= MAX_CHILDREN;

export const isMaxNested = (node, diff = 0) =>
  node.type === IRD_NODE_TYPES.ird_categories &&
  node.valid_child_types?.includes(IRD_NODE_TYPES.ird_categories) &&
  node.children?.length + diff ===
    (node?.isRoot ? MAX_CATEGORIES_NESTED_ROOT : MAX_CATEGORIES_NESTED);

export const getAddTooltip = (node, depth) => {
  if (isBeyondMaxDepth(depth)) {
    return {
      title:
        'You cannot add items to this menu item as it is beyond the maximum depth of this tree',
      disabled: true,
    };
  }

  const maxCatNested = node?.isRoot
  ? MAX_CATEGORIES_NESTED_ROOT
  : MAX_CATEGORIES_NESTED;
  if (isMaxNested(node)) {
    return {
      title: `You cannot add more categories than ${maxCatNested}`,
      disabled: true,
    };
  }

  if (isBeyondMaxChildren(node.children?.length || 0)) {
    return {
      title: `You cannot add more than ${MAX_CHILDREN} items to this tree`,
      disabled: true,
    };
  }

  return {
    title: 'Add',
    disabled: false,
  };
};
